import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  Grid,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Box,
  Typography,
  InputBase,
  InputAdornment,
  Select,
  MenuItem,
  Pagination,
  Tooltip,
} from "@mui/material";
import DownloadIcon1 from "@mui/icons-material/Download";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import axios from "axios";
import { PartnerArrowIcon } from "../../res/NewWebIcons";
import PayoutReportInvoice from "./PayoutReportInvoice";
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2pdf from "html2pdf.js";
import SearchIcon from "@mui/icons-material/Search";
import { makeStyles } from "@mui/styles";
import { Padding } from "@mui/icons-material";
import PayoutLocalInvoice from "../Reports/LocalInvoices/PayoutLocalInvoice";
import * as XLSX from 'xlsx';
import { PictureAsPdf, TableChart } from "@mui/icons-material";

const useStyles = makeStyles(() => ({
  evenRow: {
    backgroundColor: "#D0EFFF",
  },
  oddRow: {
    backgroundColor: "#FFFFFF",
  },
}));

const NewPayOutReport = () => {
  const [payOutData, setPayOutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const classes = useStyles();
  const [totalRecords, setTotalRecords] = useState(0);
  const userType = sessionStorage.getItem("uType");
  const [statusCodeValue, setStatusCodeValue] = useState(null); // State to manage status code

  const handleDownloadInvoice = async (data) => {
    const receiptElement = document.getElementById(
      `receipt-${data.txnReferenceId}`
    );

    if (!receiptElement) {
      console.error(
        "Receipt element not found:",
        `receipt-${data.txnReferenceId}`
      );
      return;
    }

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5], // top, left, bottom, right in inches
      filename: `Payout Receipt-${data.txnReferenceId}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: {
        unit: "in",
        orientation: "landscape",
        format: "a4",
        margin: 0.5,
      },
    };

    try {
      await html2pdf().from(receiptElement).set(opt).save();
    } catch (error) {
      console.error("Error generating PDF: ", error);
    }
  };

  const handleDownloadCSV = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    try {
      const response = await axios.get(
        `https://api.ipaisa.co.in/myapp/auth/allPayoutByUser`,
        {
          headers: { Authorization: `Bearer ${jwtToken}` },
          params: {
            purpose: "csv",
            ...(fromDate && { fromDate }),
            ...(toDate && { toDate }),
          },
        }
      );

      const headers = [
        "Sr. No.",
        "Txn ID",
        "Utr No.",
        "Account No.",
        "Account Name",
        "Date",
        "Time",
        "Mode",
        "Opening Balance",
        "Amount",
        "Closing Balance",
        "Status",
      ];

      // Map the response data to CSV format
      const csvRows = [
        headers.join(","), // Header row
        ...response.data.body.data.map((row, index) =>
          [
            index + 1 + currentPage * pageSize, // Sr. No.
            row.externalRef || "N/A", // Txn ID
            row.txnReferenceId || "N/A", // Utr No.
            row.payeeAccount || "N/A", // Account No.
            row.payeeName || "N/A", // Account Name
            row.timestamp ? new Date(row.timestamp).toLocaleString() : "N/A", // Date
            row.transfer_mode || "N/A", // Payment Transfer Mode
            row.walletOpeningBalance?.toFixed(2) || "N/A", // Opening Balance
            row.txnValue || "N/A", // Amount
            row.walletClosingBalance?.toFixed(2) || "N/A", // Closing Balance
            row.status || "N/A", // Status
          ].join(",")
        ),
      ].join("\n");

      // Convert the CSV data to a Blob and trigger the download
      const blob = new Blob([csvRows], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", "payout_report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value - 1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(0);
  };

  const handleSearch = () => {
    setCurrentPage(0); // Reset to the first page on new search

    // If searchQuery is empty or only whitespace, call the API with page=0 and pageSize=10
    if (!searchQuery.trim()) {
      fetchCompleteData(0, fromDate, toDate, "", 10); // Call API with no search query
    } else {
      fetchCompleteData(0, fromDate, toDate, searchQuery, pageSize); // Call API with search query
    }
  };

  // Handle search input change (no API call here)
  const handleSearchInput = (event) => {
    const updatedSearchQuery = event.target.value; // Get the updated search query value
    setSearchQuery(updatedSearchQuery); // Update search query state

    // Check if the search query length becomes 0 (i.e., the input is cleared)
    if (updatedSearchQuery.length === 0) {
      // Fetch data with default parameters when search query is cleared
      fetchCompleteData(0, "", "", "", pageSize);
    }
  };

  // Function to fetch completed data from the API
  const fetchCompleteData = async (
    page,
    fromDate,
    toDate,
    searchQuery,
    pageSize
  ) => {
    setLoading(true); // Set loading to true before making the API call

    const token = sessionStorage.getItem("jwtToken");

    // Build the URL parameters conditionally
    let url = "https://api.ipaisa.co.in/myapp/auth/allPayoutByUser?";

    // Add search query if available
    if (searchQuery.trim()) {
      url += `searchBy=${searchQuery}&`;
    }

    // Add fromDate if available
    if (fromDate) {
      url += `fromDate=${fromDate}&`;
    }

    // Add toDate if available
    if (toDate) {
      url += `toDate=${toDate}&`;
    }

    // Add pagination parameters (page and pageSize)
    url += `page=${page}&size=${pageSize}`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (
        response.status === 204 ||
        response.data.body.message === "NO_CONTENT"
      ) {
        // Handle the case when no content is returned
        console.log("No content available");
        setPayOutData([]); // Clear the data state if no content
        setStatusCodeValue(response.data.statusCodeValue);
        setTotalRecords(response.data.body.totalElements || 0); // Assuming totalRecords is provided in the response
      } else {
        setPayOutData(response.data.body.data); // Set the data to the state if content is available
        setTotalRecords(response.data.body.totalElements || 0); // Assuming totalRecords is provided in the response
      }
    } catch (error) {
      console.error("Error fetching completed transactions:", error);
      setPayOutData([]); // Optional: Clear data in case of error
    } finally {
      setLoading(false); // Set loading to false after API call
    }
  };

  useEffect(() => {
    fetchCompleteData(currentPage, fromDate, toDate, searchQuery, pageSize); // Fetch data on component mount or when page/filters change
  }, [currentPage, pageSize]); // Run when these dependencies change

  const handleClear = () => {
    setFromDate("");
    setSearchQuery("");
    setToDate("");
    setCurrentPage(0); // Reset to first page
    fetchCompleteData(0, "", "", "", pageSize); // Fetch data with default params when cleared
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true); // Show loading state
      let allData = [];
      let currentPage = 0;
      let hasMoreData = true;
      const downloadPageSize = 1000; // Fetch more data per request for download

      while (hasMoreData) {
        const response = await axios.get(
          `https://api.ipaisa.co.in/myapp/auth/allPayoutByUser`,
          {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}` },
            params: {
              page: currentPage,
              size: downloadPageSize,
              ...(fromDate && { fromDate }),
              ...(toDate && { toDate }),
              ...(searchQuery && { searchBy: searchQuery }),
            },
          }
        );

        const data = response.data.body.data;
        if (!data || data.length === 0) {
          hasMoreData = false;
        } else {
          allData = [...allData, ...data];
          currentPage++;
        }

        // If received less than page size, we've reached the end
        if (data && data.length < downloadPageSize) {
          hasMoreData = false;
        }
      }

      if (allData.length === 0) {
        console.log("No data available to download");
        setLoading(false);
        return;
      }
      
      // Format data for Excel
      const excelData = allData.map((row, index) => ({
        "Sr. No.": index + 1,
        "Txn ID": row.externalRef || "N/A",
        "Utr No.": row.txnReferenceId || "N/A",
        "Account No.": row.payeeAccount || "N/A",
        "Account Name": row.payeeName || "N/A",
        "Date": row.timestamp ? new Date(row.timestamp).toLocaleString() : "N/A",
        "Mode": row.transfer_mode || "N/A",
        "Opening Balance": row.walletOpeningBalance?.toFixed(2) || "N/A",
        "Amount": row.txnValue || "N/A",
        "Closing Balance": row.walletClosingBalance?.toFixed(2) || "N/A",
        "Remark": row.remark || "N/A",
        "Refund ID": row.refundRefId || "N/A",
        "Status": row.status || "N/A",
      }));

      // Create worksheet and workbook
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Payout Report");

      // Set column widths
      const columnWidths = [
        { wch: 8 },  // Sr. No.
        { wch: 15 }, // Txn ID
        { wch: 15 }, // Utr No.
        { wch: 15 }, // Account No.
        { wch: 20 }, // Account Name
        { wch: 20 }, // Date
        { wch: 12 }, // Mode
        { wch: 15 }, // Opening Balance
        { wch: 12 }, // Amount
        { wch: 15 }, // Closing Balance
        { wch: 20 }, // Remark
        { wch: 12 }, // Status
      ];
      worksheet['!cols'] = columnWidths;

      // Generate and download Excel file
      const currentDateTime = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `payout_report_${currentDateTime}.xlsx`);
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setLoading(false); // Hide loading state
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setLoading(true); // Show loading state
      let allPdfData = [];
      let currentPdfPage = 0;
      let hasMorePdfData = true;
      const downloadPageSize = 1000; // Fetch more data per request for download

      while (hasMorePdfData) {
        const pdfResponse = await axios.get(
          `https://api.ipaisa.co.in/myapp/auth/allPayoutByUser`,
          {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}` },
            params: {
              page: currentPdfPage,
              size: downloadPageSize,
              ...(fromDate && { fromDate }),
              ...(toDate && { toDate }),
              ...(searchQuery && { searchBy: searchQuery }),
            },
          }
        );

        const pdfPageData = pdfResponse.data.body.data;
        if (!pdfPageData || pdfPageData.length === 0) {
          hasMorePdfData = false;
        } else {
          allPdfData = [...allPdfData, ...pdfPageData];
          currentPdfPage++;
        }

        // If received less than page size, we've reached the end
        if (pdfPageData && pdfPageData.length < downloadPageSize) {
          hasMorePdfData = false;
        }
      }

      if (allPdfData.length === 0) {
        console.log("No data available to download");
        setLoading(false);
        return;
      }
      
      // Create PDF document
      const doc = new jsPDF('landscape');
      
      // Add title
      doc.setFontSize(16);
      doc.text('Payout Report', 14, 15);
      
      // Define table columns
      const columns = [
        "Sr. No.",
        "Txn ID",
        "Utr No.",
        "Account No.",
        "Account Name",
        "Date",
        "Mode",
        "Opening Balance",
        "Amount",
        "Closing Balance",
        "Remark",
        "Refund ID",
        "Status"
      ];
      
      // Prepare table rows
      const rows = allPdfData.map((row, index) => [
        index + 1,
        row.externalRef || "N/A",
        row.txnReferenceId || "N/A",
        row.payeeAccount || "N/A",
        row.payeeName || "N/A",
        row.timestamp ? new Date(row.timestamp).toLocaleString() : "N/A",
        row.transfer_mode || "N/A",
        row.walletOpeningBalance?.toFixed(2) || "N/A",
        row.txnValue || "N/A",
        row.walletClosingBalance?.toFixed(2) || "N/A",
        row.remark || "N/A",
        row.refundRefId || "N/A",
        row.status || "N/A"
      ]);
      
      // Add table to PDF
      doc.autoTable({
        head: [columns],
        body: rows,
        startY: 25,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [2, 123, 254],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
        },
      });
      
      // Download PDF
      const currentDateTime = new Date().toISOString().split('T')[0];
      doc.save(`payout_report_${currentDateTime}.pdf`);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setLoading(false); // Hide loading state
    }
  };

  return (
    <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      <Grid
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1%",
          justifyContent: "start",
        }}
      >
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "12px" }}
        >
          Reports
        </Typography>
        <PartnerArrowIcon />
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "12px" }}
        >
          Payout Report
        </Typography>
      </Grid>
      <Grid style={{ marginTop: "1%" }}>
        <Typography
          style={{
            fontWeight: "bold",
            fontFamily: "sans-serif",
            fontSize: "19px",
            color: "#343C6A",
          }}
        >
          Payout Report
        </Typography>
      </Grid>
      <Paper
        sx={{
          padding: "2rem",
          borderRadius: "8px",
          marginTop: "2%",
          overflow: "hidden", // Prevent content overflow
          boxShadow: "none",
        }}
      >
        <Grid container spacing={2}>
          {/* Date Fields */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>

          {/* Search and Buttons */}
          <Grid
            item
            xs={12}
            sx={{
              marginTop: "1%",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: "1rem",
            }}
          >
            {/* Search Field */}
            <Box
              sx={{
                display: "flex",
                flex: 1,
                maxWidth: { xs: "100%", sm: "50%" }, // Dynamic width for responsiveness
                borderRadius: "10px",
                border: "1px solid rgba(0, 0, 0, 0.3)",
                padding: "0.5rem",
              }}
            >
              <InputBase
                fullWidth
                placeholder="Search"
                value={searchQuery} // Bind the value of search input to the state
                onChange={handleSearchInput} // Only update searchQuery, no API call here
              />
              <IconButton color="primary" onClick={handleSearch}>
                {" "}
                {/* Trigger search when icon is clicked */}
                <SearchIcon />
              </IconButton>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <Button
                onClick={handleSearch}
                sx={{
                  backgroundColor: "#027BFE",
                  color: "white",
                  height: "56px",
                  textTransform: "none",
                  borderRadius: "10px",
                  width: { xs: "100%", sm: "120px" },
                }}
              >
                Search
              </Button>
              <Button
                onClick={handleClear}
                sx={{
                  backgroundColor: "gray",
                  color: "white",
                  height: "56px",
                  textTransform: "none",
                  borderRadius: "10px",
                  width: { xs: "100%", sm: "120px" },
                }}
              >
                Clear
              </Button>
              <Tooltip title="Download as Excel">
                <IconButton
                  onClick={handleDownloadExcel}
                  sx={{
                    color: "green",
                    backgroundColor: "rgba(0, 128, 0, 0.1)",
                    border: "2px solid rgba(0, 128, 0, 0.3)",
                    borderRadius: "10px",
                    height: "56px",
                    width: { xs: "100%", sm: "56px" },
                    "&:hover": {
                      backgroundColor: "green",
                      color: "white",
                    },
                  }}
                >
                  <TableChart />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download as PDF">
                <IconButton
                  onClick={handleDownloadPDF}
                  sx={{
                    color: "red",
                    backgroundColor: "rgba(255, 0, 0, 0.1)",
                    border: "2px solid rgba(255, 0, 0, 0.3)",
                    borderRadius: "10px",
                    height: "56px",
                    width: { xs: "100%", sm: "56px" },
                    "&:hover": {
                      backgroundColor: "red",
                      color: "white",
                    },
                  }}
                >
                  <PictureAsPdf />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "100vh" }}
        >
          <CircularProgress />
        </Grid>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{ marginTop: "3%", borderRadius: 2, boxShadow: "none" }}
          >
            <Table>
            <TableHead sx={{ bgcolor: "#027BFE" }}>
              <TableRow>
                {[
                  "Sr. No.",
                  "Txn ID",
                  "Utr No.",
                  "Account No.",
                  "Account Name",
                  "Date",
                  "Mode",
                  "Opening Balance",
                  "Amount",
                  "Closing Balance",
                  "Remark",
                  "Refund ID",
                  "Status",
                  "Invoice"
                ]
                  .concat(
                    userType !== "RETAILER" && userType !== "AREADISTRIBUTOR"
                      ? ["Invoice"]
                      : []
                  )
                  .map((header) => (
                    <TableCell key={header} sx={{ color: "white" }}>
                      {header}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>

              <TableBody>
                {payOutData.length > 0 ? (
                  payOutData.map((row, index) => (
                    <TableRow
                      key={index}
                      className={`${classes.tableRow} ${index % 2 === 0 ? classes.oddRow : classes.evenRow}`}
                    >
                      <TableCell>
                        {currentPage * pageSize + index + 1}
                      </TableCell>
                      <TableCell>{row.externalRef || "N/A"}</TableCell>
                      <TableCell>{row.txnReferenceId || "N/A"}</TableCell>
                      <TableCell>{row.payeeAccount || "N/A"}</TableCell>
                      <TableCell>{row.payeeName || "N/A"}</TableCell>
                      <TableCell>
                        {row.timestamp
                          ? new Date(row.timestamp).toLocaleString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {row.transfer_mode || "N/A"}
                      </TableCell>
                      <TableCell>
                        {row.walletOpeningBalance?.toFixed(2) || "N/A"}
                      </TableCell>
                      <TableCell>{row.txnValue || "N/A"}</TableCell>
                      <TableCell>
                        {row.walletClosingBalance?.toFixed(2) || "N/A"}
                      </TableCell>
                      <TableCell>{row.remark || "N/A"}</TableCell>
                      <TableCell>{row.refundRefId || 'N/A'}</TableCell>
                      <TableCell
                        align="start"
                        style={{
                          color:
                            row.status.toLowerCase() ===
                            "transaction successful"
                              ? "green"
                              : row.status.toLowerCase() === "refund"
                                ? "ad"
                                : row.status.toLowerCase() === "pending"
                                  ? "orange"
                                  : "red",
                          fontWeight: "bold",
                        }}
                      >
                        {row.status.toLowerCase() === "transaction successful"
                          ? "Successful"
                          : row.status.toLowerCase() === "refund"
                            ? "Refund"
                            : row.status.toLowerCase() === "pending"
                              ? "Pending"
                              : "Failed"}
                      </TableCell>
                      {/* {userType !== "RETAILER" && userType !== "AREADISTRIBUTOR" && ( */}
                        <TableCell align="center">
                          <DownloadIcon1
                            onClick={() => handleDownloadInvoice(row)}
                            style={{ cursor: "pointer" }}
                          />
                        </TableCell>
                      {/* )} */}

                      <Grid
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          top: "0px",
                        }}
                      >
                        <Grid id={`receipt-${row.txnReferenceId}`}>
                          {userType === "ADMIN" ? (
                            <PayoutReportInvoice data={row} />
                          ) : (
                            <PayoutLocalInvoice data={row} />
                          )}
                        </Grid>
                      </Grid>

                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No Data Available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
         {/* Pagination */}
        <Grid container spacing={2} justifyContent="space-between" alignItems="center" style={{marginTop:'1%'}}>
            <Grid item>
                <Select variant="outlined" value={pageSize} onChange={handlePageSizeChange}>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                </Select>
                <Typography display="inline" style={{ marginLeft: 8 }}>
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalRecords)} out of {totalRecords} records
                </Typography>
            </Grid>
            <Grid item>
                <Pagination count={Math.ceil(totalRecords / pageSize)} color="primary" page={currentPage + 1} onChange={handlePageChange} />
            </Grid>
        </Grid>
        </>
      )}
    </Box>
  );
};

export default NewPayOutReport;
